import { api } from '@/lib/axios'
import type { NewsItem } from '../types/news.types'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Shared Clipping API
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export interface ClipCreatedResponse {
  code: string
  path: string
}

export interface Clip {
  code: string
  keyword: string
  items: NewsItem[]
  summary: string | null
  created_at: string
  expires_at: string
}

/** One row of 「내 클리핑」 — the list never ships the articles themselves. */
export interface ClipSummary {
  code: string
  keyword: string
  item_count: number
  created_at: string
  expires_at: string
  view_count: number
}

export interface ClipListResponse {
  items: ClipSummary[]
  /** Opaque. Echo it back for the next page; `null` means this was the last one. */
  next_cursor: string | null
}

/** Matches the backend cap in `app/models/clip.py`. */
export const MAX_CLIP_ITEMS = 100

export const clipApi = {
  /**
   * Store a snapshot of one keyword's results and return its share path.
   *
   * Works signed out — the search it comes from does too. Items are trimmed to the
   * server's cap here so an oversized tab produces a shorter clipping instead of a
   * 422 the user can do nothing about.
   */
  createClip: async (input: {
    keyword: string
    items: NewsItem[]
    summary?: string | null
  }): Promise<ClipCreatedResponse> => {
    const { data } = await api.post<ClipCreatedResponse>('/api/v1/clips', {
      keyword: input.keyword,
      items: input.items.slice(0, MAX_CLIP_ITEMS).map((item) => ({
        title: item.title,
        link: item.link,
        originallink: item.originallink ?? '',
        description: item.description ?? '',
        pub_date: item.pub_date ?? '',
      })),
      summary: input.summary || null,
    })
    return data
  },

  getClip: async (code: string): Promise<Clip> => {
    const { data } = await api.get<Clip>(`/api/v1/clips/${encodeURIComponent(code)}`)
    return data
  },

  /**
   * One page of the caller's own clippings, newest first. Requires an account.
   *
   * `cursor` comes from the previous page's `next_cursor` and is never built here —
   * it carries the server's sort key, so a page number computed on the client would
   * skip rows the moment a new clipping is created.
   */
  listMyClips: async (params: { cursor?: string | null; limit?: number } = {}) => {
    const { data } = await api.get<ClipListResponse>('/api/v1/clips/mine', {
      params: {
        limit: params.limit ?? 20,
        ...(params.cursor ? { cursor: params.cursor } : {}),
      },
    })
    return data
  },
}
